import {
  MemberSessionHistoryItemEntity,
  MemberSessionHistorySummaryEntity,
} from "@/modules/members/entities/memberSessionHistoryEntity"
import {
  SESSION_HISTORY_STATUS,
  type MemberSessionHistoryItemDto,
  type MemberSessionHistorySummaryDto,
  DAY_OF_WEEK,
} from "@/modules/members/gateway/memberDto"
import dayjs from "dayjs"

// ────────────────────────────────── Item Builder ──────────────────────────────────

export class MemberSessionHistoryItemBuilder {
  private dto: MemberSessionHistoryItemDto

  static create() {
    return new MemberSessionHistoryItemBuilder()
  }

  protected constructor() {
    this.dto = {
      name: "WOD",
      color: "#FF6B6B",
      startDate: dayjs().hour(9).minute(0).second(0).toISOString(),
      endDate: dayjs().hour(10).minute(15).second(0).toISOString(),
      status: SESSION_HISTORY_STATUS.PRESENT,
      archived: false,
      offerName: "Default Offer",
      offerType: "SUBSCRIPTION",
      coachName: "Sandra Graham",
      bookedAt: dayjs().subtract(1, "day").toISOString(),
      location: null,
    }
  }

  withSessionDate(date: string) {
    this.dto.startDate = date
    return this
  }

  withDateAt(year: number, month: number, day: number, hour: number = 9, minute: number = 0) {
    this.dto.startDate = dayjs()
      .year(year)
      .month(month)
      .date(day)
      .hour(hour)
      .minute(minute)
      .second(0)
      .millisecond(0)
      .toISOString()
    return this
  }

  withProgramName(programName: string) {
    this.dto.name = programName
    return this
  }

  withProgramColor(color: string) {
    this.dto.color = color
    return this
  }

  withProgram(name: string, color: string) {
    this.dto.name = name
    this.dto.color = color
    return this
  }

  withCoach(firstname: string, lastname: string) {
    this.dto.coachName = `${firstname} ${lastname}`
    return this
  }

  withoutCoach() {
    this.dto.coachName = null
    return this
  }

  withStatus(status: MemberSessionHistoryItemDto["status"]) {
    this.dto.status = status
    return this
  }

  asAttended() {
    this.dto.status = SESSION_HISTORY_STATUS.PRESENT
    return this
  }

  asNoShow() {
    this.dto.status = SESSION_HISTORY_STATUS.NO_SHOW
    return this
  }

  build(): MemberSessionHistoryItemEntity {
    return MemberSessionHistoryItemEntity.fromDto(this.dto)
  }

  buildDto(): MemberSessionHistoryItemDto {
    return { ...this.dto }
  }
}

// ────────────────────────────────── Summary Builder ──────────────────────────────────

export class MemberSessionHistorySummaryBuilder {
  private dto: MemberSessionHistorySummaryDto

  static create() {
    return new MemberSessionHistorySummaryBuilder()
  }

  protected constructor() {
    this.dto = {
      pageCounter: { all: 0, present: 0, noShow: 0 },
      resume: [],
      favorites: [],
    }
  }

  withTotalSessions(total: number) {
    this.dto.pageCounter.all = total
    return this
  }

  withCounts(attended: number, noShow: number) {
    this.dto.pageCounter.present = attended
    this.dto.pageCounter.noShow = noShow
    this.dto.pageCounter.all = attended + noShow
    return this
  }

  withPrograms(programs: Array<{ name: string; color: string; count: number }>) {
    this.dto.resume = programs
    this.dto.pageCounter.all = programs.reduce((sum, p) => sum + p.count, 0)
    return this
  }

  withFavoriteSlots(
    slots: Array<{
      name: string
      color: string
      dayOfWeek: DAY_OF_WEEK
      startTime: string
      endTime: string
      count: number
    }>,
  ) {
    this.dto.favorites = slots
    return this
  }

  build(): MemberSessionHistorySummaryEntity {
    return MemberSessionHistorySummaryEntity.fromDto(this.dto)
  }

  buildDto(): MemberSessionHistorySummaryDto {
    return { ...this.dto }
  }
}
